import React from 'react';
import { ViewStyle } from 'react-native';
import {
  Edge,
  SafeAreaView as RNSafeAreaView,
  SafeAreaViewProps,
} from 'react-native-safe-area-context';

export interface KitSafeAreaViewProps extends Omit<SafeAreaViewProps, 'style'> {
  style?: ViewStyle | ViewStyle[];
  /**
   * The edges to apply safe area insets to. Defaults to all edges.
   * This allows fine-grained control over which edges should respect safe areas.
   */
  edges?: ReadonlyArray<Edge>;
  /**
   * The safe area inset mode. Defaults to 'padding'.
   * - 'padding': Apply safe area as padding
   * - 'margin': Apply safe area as margin
   */
  mode?: 'padding' | 'margin';
}

/**
 * A drop-in replacement for React Native's SafeAreaView that uses
 * react-native-safe-area-context for better cross-platform support,
 * especially on Android 15 with edge-to-edge enforcement.
 *
 * This component provides the same API as React Native's SafeAreaView
 * but with improved Android compatibility and additional features:
 *
 * - Better support for Android edge-to-edge mode
 * - Granular control over which edges to apply safe area to
 * - Support for both padding and margin modes
 *
 * Usage:
 * ```tsx
 * import KitSafeAreaView from '@omni/kit/components/KitSafeAreaView/KitSafeAreaView';
 *
 * // Basic usage (equivalent to React Native's SafeAreaView)
 * <KitSafeAreaView>
 *   <YourContent />
 * </KitSafeAreaView>
 *
 * // With specific edges
 * <KitSafeAreaView edges={['top', 'left', 'right']}>
 *   <YourContent />
 * </KitSafeAreaView>
 * ```
 */
const KitSafeAreaView: React.FC<KitSafeAreaViewProps> = ({
  children,
  style,
  edges = ['top', 'bottom', 'left', 'right'],
  mode = 'padding',
  ...props
}) => {
  return (
    <RNSafeAreaView style={style} edges={edges} mode={mode} {...props}>
      {children}
    </RNSafeAreaView>
  );
};

export default KitSafeAreaView;
